import { useTranslations } from 'next-intl'
import { useAuth } from '@/kmsApp/providers/keycloakServiceProvider'
import { UserCircleIcon, ChevronDownIcon } from '@heroicons/react/24/solid'
import { useUser } from '@/kmsApp/providers/user-provider'
import React, { useEffect, useRef, useState } from 'react'
import { Link } from '@onclicks/sharedmobilei18n/routing'
import { LinkMenuElement } from './LinkMenuElement'
import { CompanySubmenu } from './CompanySubmenu'
import { MeComponent } from '../MeComponent'
import { roleUserVerification } from '@/kmsApp/utils/role-user-verification'
import { NavLink } from '../HeaderClient'
import NotificationsBell from '@/kmsApp/components/features/conversation/notifications-bell'

export const ConnectionMenu = ({ links }: { links: NavLink[] }) => {
  const t = useTranslations('web_header')
  const { authenticated, logout, isReady, login, user } = useAuth()
  const { me } = useUser()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [isConnected, setIsConnected] = useState<boolean>(false)

  const mainLinks = links.filter(
    (l) => l.section === 'main' && roleUserVerification(me?.role, l.permissions),
  )
  const profileLinks = links.filter(
    (l) => l.section === 'profile-actions' && roleUserVerification(me?.role, l.permissions),
  )

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  useEffect(() => {
    setIsConnected(isReady && !!authenticated)
  }, [authenticated, isReady, user])

  if (!isReady) {
    return (
      <div className="-mr-20 pl-4 ml-8">
        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div>
      {isConnected ? (
        <div className="-mr-20 pl-4 ml-8 flex items-center gap-4">
          <NotificationsBell className="mt-1" />

          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="-m-2 inline-flex items-center justify-center rounded-md p-1 text-black"
          >
            <UserCircleIcon width={24} height={24} className="text-white" />
          </button>

          {isOpen && (
            <div
              // Le menu est positionne par rapport a l'entete (seul ancetre
              // positionne) : sans `top-full`, il retomberait sur sa position
              // statique, que le conteneur en `flex items-center` centre
              // verticalement - le haut du menu, donc le nom du compte, passant
              // alors sous la barre de navigation.
              // `max-h` + `overflow-y-auto` : avec beaucoup d'entreprises, le
              // sous-menu "Entreprise Active" peut rendre le menu plus haut que
              // l'ecran (bouton "se deconnecter" alors inaccessible).
              // `whitespace-normal` : le `<nav>` parent impose `text-nowrap`
              // (proprie CSS heritee) ; sans ce reset, les noms d'entreprise
              // longs debordent horizontalement au lieu de passer a la ligne.
              className="absolute top-full right-0 mt-2 w-64 max-h-[calc(100vh-5rem)] overflow-y-auto overflow-x-hidden whitespace-normal wrap-break-word bg-black border border-white/10 rounded-md shadow-xl z-[60] py-2 header-menu-scrollbar"
              ref={dropdownRef}
            >
              <MeComponent className="px-6 py-2 text-lg font-normal" />

              {mainLinks.map((link) => (
                <LinkMenuElement
                  key={link.href}
                  textLink={link.label}
                  linkHref={link.href}
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
                />
              ))}

              <div
                aria-hidden="true"
                className="w-full border-t border-gray-300 dark:border-white/15 mt-4"
              />
              {profileLinks.map((link) =>
                link.group === 'entreprise-active' ? (
                  // Une seule entreprise suffit : le sous-menu sert aussi à
                  // basculer « à titre privé ».
                  (me?.entreprisesDisponibles?.length ?? 0) > 0 && (
                    <CompanySubmenu
                      key={`${link.group}-${link.href}`}
                      link={link}
                      setIsOpen={setIsOpen}
                    />
                  )
                ) : (
                  <LinkMenuElement
                    key={`${link.group}-${link.href}`}
                    textLink={link.label}
                    linkHref={link.href}
                    isOpen={isOpen}
                    setIsOpen={setIsOpen}
                  />
                ),
              )}

              <LinkMenuElement
                textLink={t('logout')}
                className="text-red-500"
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                action={logout}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="-mr-20 ml-8 relative">
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="flex items-center gap-1 hover:font-bold"
          >
            <p>{t('connect')}</p>
            <ChevronDownIcon
              width={14}
              height={14}
              className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {isOpen && (
            <div
              className="absolute right-0 mt-2 w-56 bg-black border border-white/10 rounded-md shadow-xl z-[60] py-2"
              ref={dropdownRef}
            >
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false)
                  login()
                }}
                className="w-full text-left px-6 py-2 text-white hover:bg-white/10"
              >
                {t('connect')}
              </button>
              <Link
                href="/client/register"
                onClick={() => setIsOpen(false)}
                className="block w-full text-left px-6 py-2 text-white hover:bg-white/10"
              >
                {t('sign_up')}
              </Link>
              <Link
                href="/backoffice/become-partner"
                onClick={() => setIsOpen(false)}
                className="block w-full text-left px-6 py-2 text-white hover:bg-white/10"
              >
                {t('become_partner')}
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
